import React, { useState } from 'react';
import { ArrowUpRight, Sparkles, Copy, Check } from 'lucide-react';

const LeadCapture = ({ onSubmit, isLoading, couponData }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email.');
      return;
    }
    await onSubmit({ email, name }, { source: 'homepage_leadcapture' });
    setSubmitted(true);
  };

  const handleCopy = () => {
    if (!couponData?.couponCode) return;
    navigator.clipboard.writeText(couponData.couponCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <section
      data-testid="lead-capture-section"
      className="relative py-24 md:py-28 bg-onyx border-t border-b border-pewter/60"
    >
      <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-center">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Sparkles size={14} className="text-blush" />
              <span className="eyebrow">Privé Invitation</span>
            </div>
            <h2 className="font-serif text-ivory text-3xl md:text-5xl leading-[1.06] mb-6">
              Join the list. <br />
              <span className="brand-italic chrome-rose">15% off</span> your first ritual.
            </h2>
            <p className="text-platinum text-base md:text-lg leading-relaxed max-w-md">
              Receive a private concierge code for your first Privé consultation—plus curated
              wellness notes, studio openings, and quiet offers.
            </p>
          </div>

          <div className="bg-carbon border border-pewter/60 p-8 md:p-10">
            {submitted ? (
              <div className="text-center py-2" data-testid="lead-capture-success">
                <p className="eyebrow mb-4 chrome-rose">Thank you</p>
                <p className="font-serif text-ivory text-2xl mb-3">
                  You're on the <span className="brand-italic chrome-rose">list.</span>
                </p>
                <p className="text-silverx text-sm leading-relaxed mb-6">
                  Use the code below at consultation booking{' '}
                  <span className="text-platinum">— and check your inbox</span> for studio openings &amp; private offers.
                </p>

                {couponData?.couponCode && (
                  <div className="border border-blush/40 bg-onyx/60 px-5 py-5 mb-6">
                    <p className="eyebrow mb-2 text-silverx">Your Privé Code</p>
                    <div className="flex items-center justify-center gap-3">
                      <span
                        className="font-serif tracking-[0.32em] chrome-rose text-2xl md:text-3xl"
                        data-testid="lead-coupon-code"
                      >
                        {couponData.couponCode}
                      </span>
                      <button
                        type="button"
                        onClick={handleCopy}
                        aria-label="Copy code"
                        data-testid="lead-coupon-copy"
                        className="p-2 rounded-full border border-pewter/70 hover:border-blush hover:text-blush text-platinum transition-colors"
                      >
                        {copied ? <Check size={14} /> : <Copy size={14} />}
                      </button>
                    </div>
                    {couponData.discount && (
                      <p className="text-[10px] tracking-[0.28em] uppercase text-platinum/80 mt-3">
                        {couponData.discount} off your first ritual
                      </p>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="lead-capture-form">
                <div>
                  <label className="eyebrow block mb-3">Name (Optional)</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Jane Smith"
                    className="w-full bg-transparent border-b border-pewter focus:border-blush text-ivory placeholder-silverx/60 py-3 outline-none transition-colors"
                  />
                </div>
                <div>
                  <label className="eyebrow block mb-3">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="jane@example.com"
                    required
                    className="w-full bg-transparent border-b border-pewter focus:border-blush text-ivory placeholder-silverx/60 py-3 outline-none transition-colors"
                  />
                </div>
                {error && <p className="text-red-300 text-xs">{error}</p>}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="btn-pill group w-full inline-flex justify-center items-center gap-3 px-6 py-4 rounded-full bg-ivory text-onyx text-[11px] tracking-[0.28em] uppercase font-medium hover:bg-blush disabled:opacity-50 mt-2"
                >
                  {isLoading ? 'Sending…' : 'Claim 15% Code'}
                  <ArrowUpRight
                    size={14}
                    className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                  />
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default LeadCapture;
